// Change #100326 - small WhatsApp helpers shared across the inbox, tabs and settings.
import { isSystemModuleActive } from "../../helpers/functions/systemModules";

export const WHATSAPP_MODULE_CODE = "CRM_WHATSAPP";
export const isWhatsAppActive = () => isSystemModuleActive(WHATSAPP_MODULE_CODE);

/** Name of the window event HeartBeat fires for every messaging socket push. */
export const WHATSAPP_EVENT = "crmit:whatsapp";

const DIAL_CODES = { GB: "44", UK: "44", IE: "353", US: "1", CA: "1", AU: "61", NZ: "64", FR: "33", DE: "49", ES: "34", IT: "39", NL: "31" };

/**
 * Digits-only international number for wa.me ("07700 900123" -> "447700900123").
 * wa.me rejects a leading "+" and national numbers, which is why the old links failed.
 */
export const waMeDigits = (raw, defaultCountry = "GB") => {
  if (!raw) return "";
  let s = String(raw).trim().replace(/^whatsapp:/i, "").replace(/\(0\)/g, "");
  const hasPlus = s.startsWith("+");
  let digits = s.replace(/\D/g, "");
  if (!digits) return "";
  if (hasPlus) return digits;
  if (digits.startsWith("00")) return digits.slice(2);
  if (digits.startsWith("0")) return (DIAL_CODES[defaultCountry] || "44") + digits.slice(1);
  return digits.length >= 11 ? digits : (DIAL_CODES[defaultCountry] || "44") + digits;
};

export const toE164 = (raw, defaultCountry = "GB") => {
  const d = waMeDigits(raw, defaultCountry);
  return d ? `+${d}` : "";
};

export const currentUserId = () => {
  try {
    const a = JSON.parse(localStorage.getItem("authUser") || "{}");
    // Staff.Id == User.Id in a company database (AuthLoginController), so this is also the
    // id conversations are assigned to.
    return a?.get_user_data?.User_Id ?? a?.get_staff_data?.Staff_Id ?? null;
  } catch (_) {
    return null;
  }
};

/** "3h 12m" until the 24h window closes, or null when closed. */
export const windowLeftText = (seconds) => {
  const s = Number(seconds || 0);
  if (s <= 0) return null;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

/** Seconds left in the window from the last customer message time ("Y-m-d H:i:s", server local). */
export const windowSecondsFrom = (lastCustomerDateTime) => {
  if (!lastCustomerDateTime) return 0;
  const t = new Date(String(lastCustomerDateTime).replace(" ", "T")).getTime();
  if (Number.isNaN(t)) return 0;
  return Math.max(0, Math.floor((t + 86400000 - Date.now()) / 1000));
};

export const STATUS_META = {
  QUEUED: { icon: "ri-time-line", label: "Queued" },
  SENDING: { icon: "ri-time-line", label: "Sending" },
  SENT: { icon: "ri-check-line", label: "Sent" },
  DELIVERED: { icon: "ri-check-double-line", label: "Delivered" },
  READ: { icon: "ri-check-double-line", label: "Read", read: true },
  FAILED: { icon: "ri-error-warning-line", label: "Failed", failed: true },
  UNDELIVERED: { icon: "ri-error-warning-line", label: "Not delivered", failed: true },
};

export const formatTime = (dt) => {
  if (!dt) return "";
  const d = new Date(String(dt).replace(" ", "T"));
  if (Number.isNaN(d.getTime())) return "";
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  const yesterday = new Date(today.getTime() - 86400000).toDateString() === d.toDateString();
  const time = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  if (sameDay) return time;
  if (yesterday) return `Yesterday ${time}`;
  return `${d.toLocaleDateString([], { day: "numeric", month: "short" })} ${time}`;
};

export const formatDay = (dt) => {
  const d = new Date(String(dt).replace(" ", "T"));
  if (Number.isNaN(d.getTime())) return "";
  const today = new Date();
  if (d.toDateString() === today.toDateString()) return "Today";
  if (new Date(today.getTime() - 86400000).toDateString() === d.toDateString()) return "Yesterday";
  return d.toLocaleDateString([], { weekday: "long", day: "numeric", month: "long", year: d.getFullYear() === today.getFullYear() ? undefined : "numeric" });
};

export const placeholdersIn = (text) => {
  const out = [];
  String(text || "").replace(/{{\s*(\d+)\s*}}/g, (_, n) => {
    if (!out.includes(n)) out.push(n);
    return "";
  });
  return out.sort((a, b) => Number(a) - Number(b));
};

export const renderTemplate = (text, vars = {}) =>
  String(text || "").replace(/{{\s*(\d+)\s*}}/g, (m, n) => (vars[n] ? vars[n] : m));
